import root from "react-shadow";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import React from "react";
import { botIcons } from "@/components/dialog/ChatbotDialog";
import {
  ChatBotProps,
  Msg, Source, msgActionStateSending, msgActionStateSent,
  msgActionVisitorData,
  roleAssistant,
  roleLeadForm,
  roleUser, msgActionReaction,
} from "@/components/dialog/types";
import { contrastColor, hexToRGB } from "@/utils/colors";
import {Icon} from "@/components/Icon.tsx";
import {timeAgo} from "@/utils/utils";

const markdownStyles = (textColor: string) => {
  return `
table, th, td {border: 1px solid; padding : 0.5rem;}
table {border-collapse: collapse; margin-top: 0.5rem; margin-bottom: 0.5rem; }
.markdown-container p {white-space : pre-wrap; margin-top: 0.5rem; margin-bottom: 0.5rem; }
.markdown-container a {color : ${textColor}
`;
};

let profileIconColor: string = "";
let profileIconBgColor: string = "";
let msgAssistantTextColor: string = "";
let msgUserTextColor: string = "";
let msgAssistantColor = ""; // prev val
let msgUserColor = ""; // prev val

export const profileIconRenderer = (props: ChatBotProps, scale? : number, styles? : string) => {
  let size = scale ? 10 * scale : 10;

  if (!profileIconColor || profileIconBgColor !== props.design.profileIconBgColor) {
    profileIconBgColor = props.design.profileIconBgColor
    let rgb = hexToRGB(profileIconBgColor);
    profileIconColor = contrastColor(rgb.r, rgb.g, rgb.b);
  }

  return (
    <div>
      {props.design.profileImg === "" && props.design.profileIcon !== "" && (
        <div
          className={`rounded-full w-${size} h-${size} flex justify-center items-center ${styles}`}
          style={{
            backgroundColor: props.design.profileIconBgColor,
            color: profileIconColor,
          }}
        >
          {botIcons(scale)[props.design.profileIcon].icon}
        </div>
      )}

      {props.design.profileImg !== "" && (
        <img className={`rounded-full w-${size} h-${size}`} src={props.design.profileImg} />
      )}
    </div>
  );
};
export const msgRenderer = (
  props: ChatBotProps,
  msg: Msg,
  msgWidth: number,
  msgIndex: number,
  onAction: any,
  onChange: any,
) => {
  if (
    !msgAssistantTextColor ||
    msgAssistantColor != props.design.msgAssistantColor
  ) {
    msgAssistantColor = props.design.msgAssistantColor;
    let rgb = hexToRGB(props.design.msgAssistantColor);
    msgAssistantTextColor = contrastColor(rgb.r, rgb.g, rgb.b);
  }
  if (!msg) {
    return
  }

  if (!msgUserTextColor || msgUserColor != props.design.msgUserColor) {
    msgUserColor = props.design.msgUserColor;
    let rgb = hexToRGB(props.design.msgUserColor);
    msgUserTextColor = contrastColor(rgb.r, rgb.g, rgb.b);
  }

  if (msg.role === roleAssistant) {
    let condProps = {
      key: msg.id,
      className: "flex mb-4 pr-4",
    } as any;

    let manySources = msg.sources && msg.sources.length > 1;

    return (
      <div {...condProps}>
        {props.design.showProfileIconMessage && (
          <div className="flex-none flex flex-col items-center space-y-1 mr-4">
            {profileIconRenderer(props)}

            <span className="block text-xs">{msg.name}</span>
          </div>
        )}
        <div className="wz-msg-text flex flex-col items-start flex-1 relative max-w-[650px]">
          <div
            className={"rounded-lg " + (msg.text && msg.sources ? "min-w-[230px]" : "")}
            style={{
              color: msgAssistantTextColor,
              backgroundColor: props.design.msgAssistantColor,
              overflowWrap: "anywhere",
            }}
          >
            {textRenderer(props, msg)}

            {msg.streaming !== undefined && !msg.streaming &&
              <div className="relative min-h-[40px]">
                {props.design.showResponseWebLinks &&
                  sourcesRenderer(msg, onChange)
                }

                <div className={"min-h-[40px] top-0 h-full flex justify-end items-center " + (!msg.sourcesExpanded ? "absolute right-2" : "px-2")}>
                  {(!msg.reaction || msg.reaction == "positive") &&
                    <div className={"border rounded py-1 px-1.5 mr-1 hover:bg-white hover:text-green-500 " + (msg.reaction == "positive" && "bg-white text-green-500")}
                         onClick={() => {
                           let newMsg = Object.assign({}, msg)
                           newMsg.reaction = "positive"
                           onAction(msgActionReaction, newMsg);
                         }}
                    >
                      <Icon icon="fa:thumbs-o-up"/>
                    </div>
                  }
                  {(!msg.reaction || msg.reaction == "negative") &&
                    <div className={"border rounded py-1 px-1.5 hover:bg-white hover:text-red-400 " + (msg.reaction == "negative" && "bg-white text-red-400")}
                         onClick={() => {
                           let newMsg = Object.assign({}, msg)
                           newMsg.reaction = "negative"
                           onAction(msgActionReaction, newMsg);
                         }}
                    >
                      <Icon icon="fa:thumbs-o-down" />
                    </div>
                  }
                </div>

              </div>
            }
          </div>
          <div className="text-xs opacity-60 pl-1 py-1">
            {msg.time && timeAgo(new Date(msg.time*1000))}
          </div>

          {/*Buttons ----------------------------------------------------------------------------------*/}
          {!!msg.buttons && props.design.showResponseWebLinks && (
            <div className="w-full flex justify-between text-xs">
              <div
                style={{
                  width: `${
                    msgWidth - (!msg.sourcesExpanded && manySources ? 50 : 0)
                  }px`,
                }}
                className={
                  "relative flex overflow-hidden " +
                  (msg.sourcesExpanded ? "flex-col" : "")
                }
              >
                {!msg.sourcesExpanded && manySources && (
                  <div
                    className="absolute top-0 right-0 w-[5%] h-full"
                    style={{
                      background:
                        "linear-gradient(90deg, transparent 0%, #ffffff 100%)",
                    }}
                  ></div>
                )}
                {msg.buttons.map((button: any, i: number) => {
                  return (
                    <div
                      key={i}
                      className={
                        "flex items-center border border-slate-300 hover:bg-slate-100 px-2 rounded-md mr-2 " +
                        (msg.sourcesExpanded && i !== 0 ? "mt-2 " : "mr-2 ") +
                        (!manySources ? "max-w-[100%]" : "")
                      }
                    >
                      <a
                        href={button.url}
                        target="_blank"
                        className="block w-full p-1"
                        title={button.url}
                        style={{
                          textOverflow: "ellipsis",
                          overflow: "hidden",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {button.title}
                      </a>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (msg.role === roleUser) {
    return (
      <div
        key={msg.id}
        className="flex items-center flex-row-reverse mb-4 pl-4"
      >
        {!!msg.avatar && (
          <div className="flex-none flex flex-col items-center space-y-1 ml-4">
            <img className="rounded-full w-10 h-10" src={msg.avatar} />
            <span className="block text-xs">{msg.name}</span>
          </div>
        )}
        <div>
          <div
            className="flex flex-col items-start p-2 pl-3 rounded-lg relative"
            style={{
              color: msgUserTextColor,
              backgroundColor: props.design.msgUserColor,
              overflowWrap: "anywhere",
            }}
          >
            {msg.text.split("\n").map((line: string, lineIndex: number) => {
              return <div key={lineIndex}>{line}</div>;
            })}
            <div
              className="absolute right-0 transform translate-x-1/2 rotate-45 w-2 h-2"
              style={{ backgroundColor: props.design.msgUserColor }}
            />
          </div>
          <div className="flex justify-end text-xs opacity-60 pr-1 py-1">
            {msg.time && timeAgo(new Date(msg.time*1000))}
          </div>
        </div>
      </div>
    );
  }

  if (msg.role === roleLeadForm) {
    let condProps = {
      key: msg.id,
      className: "flex mb-4 pr-4",
    } as any;

    return (
      <div {...condProps}>
        {props.design.showProfileIconMessage && (
          <div className="flex-none flex flex-col items-center space-y-1 mr-4">
            {profileIconRenderer(props)}

            <span className="block text-xs">{msg.name}</span>
          </div>
        )}
        <div className="wz-msg-text flex flex-col items-start flex-1 relative max-w-[650px]">
          <div
            className="rounded-lg mb-2"
            style={{
              color: msgAssistantTextColor,
              backgroundColor: props.design.msgAssistantColor,
              overflowWrap: "anywhere",
            }}
          >
            {textRenderer(props, msg)}

            <div className="px-3 text-gray-800 pt-2 flex flex-col items-end">
              {msg.formFields?.map((field, i) => {
                return (
                  <div key={i} className="w-full">
                    <span
                      className="text-sm"
                      style={{ color: msgAssistantTextColor }}
                    >
                      {field.title}
                    </span>
                    <input
                      value={field.value ? field.value : ""}
                      onChange={(event) => {
                        if (!msg.formFields) {
                          return;
                        }
                        for (
                          let fidx = 0;
                          fidx < msg.formFields.length;
                          fidx++
                        ) {
                          if (msg.formFields[fidx].title === field.title) {
                            msg.formFields[fidx].value = event.target.value;
                            break;
                          }
                        }
                        onChange(msg);
                      }}
                      className={
                        "mb-3 w-full rounded border border-gray-200 outline-0 p-1.5"
                      }
                      type="text"
                    />
                  </div>
                );
              })}

              <div className="flex flex-row items-center mb-3">
                {msg.actionState == msgActionStateSending &&
                    <Icon icon="fluent:arrow-sync-24-regular" className="animate-spin mr-2" width="25" style={{color: msgAssistantTextColor}} />
                }
                {msg.actionState == msgActionStateSent &&
                    <Icon icon="fluent:checkmark-24-regular" className="mr-2" width="25" style={{color: msgAssistantTextColor}} />
                }
                <button
                  type="button"
                  className="relative rounded text-sm"
                  style={{
                    color: msgAssistantTextColor,
                    borderColor: msgAssistantTextColor,
                    borderWidth: "1px",
                  }}
                  onClick={() => {
                    onAction(msgActionVisitorData, msg);
                  }}
                >
                  <div
                    className="absolute w-full h-full opacity-0 hover:opacity-20"
                    style={{ backgroundColor: msgAssistantTextColor }}
                  ></div>
                  <div className="w-full h-full flex items-center px-3 py-1">
                    Send
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
};

const textRenderer = (props: ChatBotProps, msg: Msg) => {
  return (
    <div className="px-3">
      {msg.text && (
        <root.div>
          <style type="text/css">{markdownStyles(msgAssistantTextColor)}</style>
          <div className="markdown-container">
            <ReactMarkdown
              children={msg.text}
              remarkPlugins={[remarkGfm]}
              components={{
                a: ({ node, ...props }) => {
                  return (
                    <a {...props} target="_blank">
                      {props.children}
                    </a>
                  );
                },
              }}
            />
          </div>
        </root.div>
      )}
      {!msg.text && (
        <div className="min-h-[40px] min-w-[50px] flex items-center justify-center">
          <div
            className="dot-flashing dot-left"
            style={{
              color: msgAssistantTextColor,
              backgroundColor: msgAssistantTextColor,
            }}
          ></div>
          <div
            className="dot-flashing"
            style={{
              color: msgAssistantTextColor,
              backgroundColor: msgAssistantTextColor,
            }}
          ></div>
          <div
            className="dot-flashing dot-right"
            style={{
              color: msgAssistantTextColor,
              backgroundColor: msgAssistantTextColor,
            }}
          ></div>
        </div>
      )}
    </div>
  );
};

const sourcesRenderer = (msg : Msg, onchange : any) => {
    if (!msg.sources || msg.sourceID === undefined) {
        return (<div></div>)
    }

    if (msg.sources && !msg.sourcesExpanded) {
        return (
            <div className="flex">
              <div className="flex items-center py-2 pl-2 cursor-pointer hover:opacity-60"
                onClick={() => {
                  msg.sourcesExpanded = true
                  onchange(msg)
                }}
              >
                <Icon className="inline mr-1" icon='fluent:info-24-regular' width="20px" />
                Show sources
              </div>
            </div>
        )
    }


  let src : Source = msg.sources[msg.sourceID];

  const sourceChange = (next : boolean) => {
      if (msg.sourceID === undefined || !msg.sources) {
          return
      }
      let nextID = next ? msg.sourceID+1 : msg.sourceID-1;
      nextID = nextID === msg.sources.length ? 0 : nextID;
      nextID = nextID < 0 ? msg.sources.length-1 : nextID;

      msg.sourceID = nextID;
      onchange(msg)
  }

  return (
    <div className="my-4 px-3">
        <a href={src.url} target="_blank">
            <div className="font-semibold">
                {src.favicon &&
                    <img className="max-h-[20px] mr-1 inline align-middle" src={src.favicon} />
                }
                {src.title}
            </div>
            <div className="text-sm py-1">
                {src.description && !src.description.includes("<!--HTML-->") &&
                    <span>{src.description.length > 80 ? src.description.slice(0, 90) + "..." : src.description}</span>
                }

                {src.description && src.description.includes("<!--HTML-->") &&
                    <div dangerouslySetInnerHTML={{__html: src.description}} />
                }
            </div>
            {src.image &&
                <div className="flex justify-center">
                    <img className="max-h-[15vh]" src={src.image} />
                </div>
            }
            <div className="mt-1">
                <Icon className="inline mr-1" icon='ph:link' />
                <span className="break-all">{src.url}</span>
            </div>
        </a>
        {msg.sources.length > 1 &&
            <div className="flex justify-between mt-2 select-none">
                <Icon
                    onClick={() => {sourceChange(false)}}
                    className="hover:opacity-60 cursor-pointer" icon='fluent:ios-arrow-ltr-24-filled' width="24px" />
                <span>{`Sources: ${msg.sourceID+1}/${msg.sources.length}`}</span>
                <Icon
                    onClick={() => {sourceChange(true)}}
                    className="hover:opacity-60 cursor-pointer" icon='fluent:ios-arrow-rtl-24-filled' width="24px" />
            </div>
        }
    </div>
  )
}